import { EXCEPTION_TYPE } from 'settings/constants/exceptionType'

const toDisplayString = (value: unknown): string => {
  if (value == null || typeof value === 'object') return ''
  return String(value)
}

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {}

const firstDisplayValue = (...values: unknown[]): string => values.map(toDisplayString).find(Boolean) ?? ''

/**
 * Mobile writes raw dictionary codes ("1", "101") into fields the web renders as
 * labeled options. A bare code is meaningless to users, so callers hide it from
 * display surfaces (the stored value itself is preserved so saves don't strip it).
 */
export const isRawMobileCode = (value: unknown): boolean => /^\d+$/.test(String(value ?? '').trim())

/**
 * Per-type captured value with the mobile fallbacks: the web writes the flat
 * `description`, the driver app writes the equivalent under `metadata.*`. Single
 * source for the card pill and the drawer prefill so both always agree.
 */
export const getExceptionDescriptionValue = (exception: unknown): string => {
  const record = asRecord(exception)
  const metadata = asRecord(record.metadata)
  if (record.exceptionType === EXCEPTION_TYPE.DAMAGE) {
    return firstDisplayValue(record.description, metadata.selected_damage_type, metadata.damage_type)
  }
  if (record.exceptionType === EXCEPTION_TYPE.WEIGHT) return firstDisplayValue(record.description, metadata.weight)
  if (record.exceptionType === EXCEPTION_TYPE.SERIAL_NUMBER) return firstDisplayValue(record.description, metadata.serial_number)
  if (record.exceptionType === EXCEPTION_TYPE.SPECIAL) return firstDisplayValue(record.description, metadata.selected_special)
  if (record.exceptionType === EXCEPTION_TYPE.EXTRA_ITEM) return firstDisplayValue(record.description, metadata.extra)
  return firstDisplayValue(record.description)
}

/**
 * Damage responsible party. No `metadata.responsible_party` fallback: the work-order and exception reads
 * inflate the mobile code server-side, and reading it here would re-surface codes the server dropped —
 * which the edit form would resubmit. The work-order *list* payload omits the field entirely, so that
 * surface shows nothing rather than a bare code.
 */
export const getExceptionResponsibleParty = (exception: unknown): string =>
  firstDisplayValue(asRecord(exception).responsibleParty)

/**
 * Delay elapsed milliseconds with the web-write fallback: mobile persists the BE `delayTime`
 * column, but the web create/update DTOs carry no delay field, so the web stores the elapsed ms
 * in `description` (the per-type value channel). Single source for the card's timer pill and
 * the drawer prefill so both always agree.
 */
export const getExceptionDelayMs = (exception: unknown): number => {
  const record = asRecord(exception)
  const delayTime = Number(record.delayTime)
  if (Number.isFinite(delayTime) && delayTime > 0) return delayTime
  if (record.exceptionType !== EXCEPTION_TYPE.DELAY) return 0
  const fromDescription = Number(firstDisplayValue(record.description, asRecord(record.metadata).delay_time))
  return Number.isFinite(fromDescription) && fromDescription > 0 ? fromDescription : 0
}
